<template>
  <div>
    <DxForm id="formularioRetrocomisionEnergia" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" :visible="model != null" validation-group="validacionRetrocomisionEnergia">
      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="4" :sm="4" :md="4" :lg="4" />

      <DxGroupItem :col-span="4" :col-count="4">
        <DxSimpleItem data-field="Importe" editor-type="dxNumberBox" :col-span="2" :editor-options="importeNumberOptions">
          <DxLabel :text="$t('retrocomisiones.form.importe')" />
          <DxRequiredRule :message="$t('retrocomisiones.form.validacion.importe')" />
          <DxCompareRule :comparison-target="() => 0" :comparison-type="'>='"
            :message="$t('retrocomisiones.form.validacion.importe_mayor_cero')" />
          <DxCompareRule :comparison-target="() => 100" :comparison-type="'<='"
            :message="$t('retrocomisiones.form.validacion.importe_menor_cien')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="PeriodoHasta" editor-type="dxNumberBox" :col-span="2" :editor-options="periodoHastaNumberOptions">
          <DxLabel :text="$t('retrocomisiones.form.periodo_hasta')" />
          <DxRequiredRule :message="$t('retrocomisiones.form.validacion.periodo_hasta')" />
          <DxCompareRule :comparison-target="() => 0" :comparison-type="'>='"
            :message="$t('retrocomisiones.form.validacion.periodo_hasta_mayor_igual_cero')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="TarifaAcceso" editor-type="dxTagBox" :col-span="4" :editor-options="{ ...selectTarifaAccesoOptions}">
          <DxLabel template="tarifaAccesoLabel"/>
        </DxSimpleItem>
        <DxGroupItem :col-span="4" :col-count="4">
          <DxSimpleItem data-field="AplicaPersonaFisica" :col-span="4">
           <DxCheckBox v-model="props.model!.AplicaPersonaFisica"
             :text="$t('retrocomisiones.form.aplica_persona_fisica')" validation-message-mode="always"
             validation-message-position="right">
             <DxValidator ref="validatorAplicaPersona" validation-group="validacionAplicaPersona">
              <DxCustomRule validation-group="validacionAplicaPersona"
                :validation-callback="validateAlMenosUnoSeleccionado" message="" :reevaluate="true"/>
             </DxValidator>
           </DxCheckBox>
           <DxLabel :visible="false" />
         </DxSimpleItem>
          <DxSimpleItem data-field="AplicaPersonaJuridica" :col-span="4">
            <DxCheckBox v-model="props.model!.AplicaPersonaJuridica"
              :text="$t('retrocomisiones.form.aplica_persona_juridica')" validation-message-mode="always"
              validation-message-position="right">
              <DxValidator ref="validatorAplicaPersona" validation-group="validacionAplicaPersona">
                <DxCustomRule validation-group="validacionAplicaPersona"
                  :validation-callback="validateAlMenosUnoSeleccionado"
                  :message="$t('retrocomisiones.form.validacion.al_menos_uno_seleccionado')" :reevaluate="true"/>
               </DxValidator>
            </DxCheckBox>
            <DxLabel :visible="false" />
          </DxSimpleItem>
        </DxGroupItem>
      </DxGroupItem>

      <template #tarifaAccesoLabel="{ data }">
        <div id="template-content">
          <span class="dx-field-item-label-text">{{ $t('retrocomisiones.form.tarifa_acceso') }}</span>
          <div class="info-icon d-inline text-primary ms-1">
            <i class="fas fa-info-circle" id="whitelistLabel"></i>
            <DxTooltip :hide-on-outside-click="false" target="#whitelistLabel" show-event="mouseenter"
              hide-event="mouseleave">
              <template #content >
                <div id="tooltip-content">{{ $t('retrocomisiones.form.texto_informativo_tarifa_acceso') }}</div>
              </template>
            </DxTooltip>
          </div>
        </div>
      </template>
    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { type CreateCriterioRetrocomisionEnergiaModel, type EditCriterioRetrocomisionEnergiaModel } from '@api/api';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import {
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxCompareRule,
  DxSimpleItem,
  DxGroupItem
} from "devextreme-vue/form";
import { getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useIntl } from '@services/i18n';
import { getLookup } from '@helpers/grid-helper';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { DxCheckBox, DxTooltip } from "devextreme-vue";
import type { CreateRetrocomisionEnergia } from '@/components/retrocomisiones-energia/models/CreateRetrocomisionEnergia';
import DxValidator, { DxCustomRule } from 'devextreme-vue/validator';

const api = useApi();
const formInstance = ref<DxForm>();
const intl = useIntl();
const props = defineProps({
  model: {
    type: Object as () => CreateRetrocomisionEnergia,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  }
});

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async () => {
  var res1 = validationEngine.validateGroup("validacionRetrocomisionEnergia");
  var res2 = validationEngine.validateGroup("validacionAplicaPersona");

  if (res1.isValid && res2.isValid) {
    if (props.isEditMode) {
      await api.callStandardApi("editCriterioRetrocomisionEnergia", { showLoading: true }, props.model.IdEmpresaServicios, props.model as EditCriterioRetrocomisionEnergiaModel)
        .then(() => toastSuccess(intl.i18n.t('retrocomisiones.edicion_exito')))
        .catch(() => {
          toastError(intl.i18n.t('retrocomisiones.edicion_fallo'))
          return false;
        });
    } else {
      await api.callStandardApi("createCriterioRetrocomisionEnergia", { showLoading: true }, props.model.IdEmpresaServicios, props.model as CreateCriterioRetrocomisionEnergiaModel)
        .then(() => toastSuccess(intl.i18n.t('retrocomisiones.creacion_exito')))
        .catch(() => {
          toastError(intl.i18n.t('retrocomisiones.creacion_fallo'));
          return false;
        });
    }
    return true;
  } else {
    toastError(intl.i18n.t('retrocomisiones.revise_validacion'));
  }

  return false;
}

const periodoHastaNumberOptions = {
  showSpinButtons: true,
  step: 1,
  format: {
    type: "fixedPoint",
    precision: 0,
    formatter: (value: number) => {
      if (value == 1)
        return value + ' ' + intl.i18n.t('retrocomisiones.form.mes');
      else
        return value + ' ' + intl.i18n.t('retrocomisiones.form.meses');
    }
  },
  min: 0
};

const importeNumberOptions = {
  showSpinButtons: true,
  step: 0.01,
  format: {
    type: "fixedPoint",
    precision: 2,
    formatter: (value: number) => {
      return intl.i18n.n(Number(value.toFixed(2)), 'decimal') + ' %';
    }
  },
  min: 0,
  max: 100
};

const selectTarifaAccesoOptions = {
  showClearButton: true,
  dataSource: getLookup('retrocomisiones', 'tarifa_acceso').dataSource,
  valueExpr: 'text',
  displayExpr: 'text'
}

const validateAlMenosUnoSeleccionado = (e: any) => {
  const aplicaPersonaFisica = props.model.AplicaPersonaFisica || false;
  const aplicaPersonaJuridica = props.model.AplicaPersonaJuridica || false;
  
  return aplicaPersonaFisica || aplicaPersonaJuridica;
};

watch(
  () => ({
    aplicaPersonaFisica: props.model.AplicaPersonaFisica,
    aplicaPersonaJuridica: props.model.AplicaPersonaJuridica
  }),
  () => {
    validationEngine.validateGroup("validacionAplicaPersona");
  }
)

</script>
<style lang="scss" scoped></style>
